import { useCallback, useEffect, useReducer, useRef, useState, type KeyboardEvent } from 'react';
import { describeError } from '../api.ts';
import { useRouter } from '../router.tsx';
import { announceChange } from '../ui/changes.ts';
import { Kbd } from '../ui/kit.tsx';
import { useToast } from '../ui/toasts.tsx';
import { companionApi, newConversationId } from './api.ts';
import { cellText, confirmPayload, gridCommand, gridReducer, gridSummary, initialGrid, selectedIds, type GridState } from './grid.ts';
import { GRID_COMPONENTS, gridFields } from './grids.tsx';
import type { CompanionStatus, Proposal } from './types.ts';
import './companion.css';

interface LogEntry {
  id: number;
  kind: 'user' | 'assistant' | 'tool' | 'error' | 'note';
  text: string;
  status?: 'running' | 'ok' | 'error';
  toolId?: string;
}

const EMPTY_GRID: GridState = { rows: [], cursor: 0, column: 0, anchor: null, editing: null };

export const COMPANION_SHORTCUTS: [string, string][] = [
  // Key labels are unique in the help table (they are its React keys), hence the "grid:" prefixes.
  ['.', 'Open the companion (from any page)'],
  ['Enter / shift + Enter in the prompt', 'Companion: send / new line'],
  ['grid: j / k, shift + j / k', 'Companion grid: move, extend the selection'],
  ['grid: h / l', 'Companion grid: previous / next column'],
  ['grid: Enter', 'Companion grid: edit the cell (Enter keeps it, Esc cancels)'],
  ['grid: x', 'Companion grid: include / exclude the selected rows'],
  ['grid: a', 'Companion grid: include all rows without errors'],
  ['grid: r', 'Companion grid: ask the companion to redo the selected rows with your note'],
  ['grid: cmd / ctrl + Enter', 'Companion grid: confirm and save'],
  ['grid: Esc', 'Companion grid: discard the proposal (Esc again closes the companion)'],
  ['u', 'Companion: undo the last saved change (for a few minutes)'],
  ['i', 'Companion: back to the prompt'],
];

/** The Studio companion: chat on the right, proposals as an editable grid, everything by keyboard. */
export function CompanionPanel({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const { path } = useRouter();
  const [status, setStatus] = useState<CompanionStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [history, setHistory] = useState<{ role: 'user' | 'assistant'; text: string }[]>([]);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [grid, dispatch] = useReducer(gridReducer, EMPTY_GRID);
  const [redo, setRedo] = useState<string | null>(null);
  const [saved, setSaved] = useState<Proposal | null>(null);
  const [now, setNow] = useState(Date.now());
  const conversation = useRef(newConversationId());
  const nextId = useRef(1);
  const input = useRef<HTMLTextAreaElement>(null);
  const gridBox = useRef<HTMLDivElement>(null);
  const logBox = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const panel = useRef<HTMLElement>(null);

  const add = useCallback((entry: Omit<LogEntry, 'id'>) => setLog((list) => [...list, { ...entry, id: nextId.current++ }]), []);

  useEffect(() => {
    previousFocus.current = document.activeElement as HTMLElement | null;
    input.current?.focus();
    companionApi.status().then(setStatus, (error: unknown) => setStatusError(describeError(error)));
    companionApi.undoable().then(setSaved, () => undefined);
    return () => previousFocus.current?.focus?.();
  }, []);

  useEffect(() => {
    logBox.current?.scrollTo({ top: logBox.current.scrollHeight });
  }, [log]);

  // The undo countdown.
  useEffect(() => {
    if (!saved?.undoUntil) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [saved]);
  const undoLeft = saved?.undoUntil ? Math.max(0, Date.parse(saved.undoUntil) - now) : 0;

  const showProposal = useCallback((next: Proposal | null) => {
    setProposal(next);
    if (next) dispatch({ type: 'reset', proposal: next });
    setRedo(null);
    if (next) window.setTimeout(() => gridBox.current?.focus(), 0);
  }, []);

  const send = async () => {
    const message = prompt.trim();
    if (!message || busy) return;
    if (!status?.configured) {
      add({ kind: 'error', text: status?.reason ?? 'The companion is not configured.' });
      return;
    }
    setPrompt('');
    setBusy(true);
    add({ kind: 'user', text: message });
    const answers: string[] = [];
    try {
      await companionApi.chat({ message, history, conversation: conversation.current, page: path }, (event) => {
        if (event.type === 'text') {
          answers.push(event.text);
          add({ kind: 'assistant', text: event.text });
        } else if (event.type === 'tool') {
          setLog((list) => {
            const existing = list.find((entry) => entry.toolId === event.id);
            const text = event.summary ? `${event.label}: ${event.summary}` : `${event.label}…`;
            if (existing) return list.map((entry) => (entry.toolId === event.id ? { ...entry, text, status: event.status } : entry));
            return [...list, { id: nextId.current++, kind: 'tool', text, status: event.status, toolId: event.id }];
          });
        } else if (event.type === 'proposal') {
          showProposal(event.proposal);
        } else if (event.type === 'error') {
          add({ kind: 'error', text: event.message });
        }
      });
      setHistory((list) => [...list, { role: 'user' as const, text: message }, { role: 'assistant' as const, text: answers.join('\n') || '(no answer)' }].slice(-20));
    } catch (error) {
      add({ kind: 'error', text: describeError(error) });
    } finally {
      setBusy(false);
      if (!gridBox.current) input.current?.focus();
    }
  };

  const confirm = async () => {
    if (!proposal || proposal.status !== 'proposed' || busy) return;
    const summary = gridSummary(proposal, grid);
    if (summary.changed === 0) {
      toast('info', 'Nothing to save: no included row changes anything');
      return;
    }
    setBusy(true);
    try {
      const result = await companionApi.confirm(proposal.ref, confirmPayload(grid));
      if (result.status === 'applied') {
        toast('success', `Saved ${result.applied ?? summary.changed} change${result.applied === 1 ? '' : 's'}`);
        add({ kind: 'note', text: `Saved ${result.applied} change(s) (${result.ref}). Press u to undo while the timer runs, i to write again.` });
        announceChange();
        setSaved(result);
        setNow(Date.now());
        showProposal(null);
        panel.current?.focus();
      } else {
        add({ kind: 'error', text: `Not saved: ${result.error ?? result.status}. Nothing was changed.` });
        setProposal(result);
      }
    } catch (error) {
      add({ kind: 'error', text: describeError(error) });
      // Errors found on confirm are stored on the proposal: show them.
      const fresh = await companionApi.proposal(proposal.ref).catch(() => null);
      if (fresh) showProposal(fresh);
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!proposal) return;
    if (proposal.status === 'proposed') {
      await companionApi.discard(proposal.ref).catch((error: unknown) => add({ kind: 'error', text: describeError(error) }));
      add({ kind: 'note', text: `Discarded ${proposal.ref}; nothing was changed.` });
    }
    showProposal(null);
    panel.current?.focus();
  };

  const undo = async () => {
    if (!saved || undoLeft <= 0 || busy) {
      toast('info', 'Nothing to undo');
      return;
    }
    setBusy(true);
    try {
      const result = await companionApi.undo(saved.ref);
      toast('success', `Undone: ${saved.ref}`);
      add({ kind: 'note', text: `Undid ${saved.ref} (${result.ref}).` });
      announceChange();
      setSaved(null);
    } catch (error) {
      add({ kind: 'error', text: describeError(error) });
    } finally {
      setBusy(false);
    }
  };

  const submitRedo = async () => {
    if (!proposal || redo === null || !redo.trim()) return;
    const ids = selectedIds(grid);
    setBusy(true);
    try {
      const result = await companionApi.redo(proposal.ref, ids, redo.trim(), conversation.current);
      if (result.text) add({ kind: 'assistant', text: result.text });
      showProposal(result.proposal);
    } catch (error) {
      add({ kind: 'error', text: describeError(error) });
      setRedo(null);
      gridBox.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const fields = proposal ? gridFields(proposal) : [];
  const readOnly = !proposal || proposal.status !== 'proposed' || busy;

  const onGridKey = (event: KeyboardEvent) => {
    if (event.target !== gridBox.current || !proposal) return;
    const command = gridCommand(event);
    if (!command) return;
    event.preventDefault();
    event.stopPropagation();
    switch (command) {
      case 'down':
      case 'up':
      case 'extendDown':
      case 'extendUp':
        dispatch({ type: 'move', delta: command === 'down' || command === 'extendDown' ? 1 : -1, extend: command.startsWith('extend') });
        break;
      case 'left':
      case 'right':
        dispatch({ type: 'column', delta: command === 'left' ? -1 : 1, fields: fields.length });
        break;
      case 'edit': {
        const field = fields[grid.column];
        const row = grid.rows[grid.cursor];
        if (!readOnly && field && row) dispatch({ type: 'startEdit', field, value: cellText(field, row.proposed[field]) });
        break;
      }
      case 'toggle':
        if (!readOnly) dispatch({ type: 'toggle' });
        break;
      case 'acceptAll':
        if (!readOnly) dispatch({ type: 'acceptAll' });
        break;
      case 'redo':
        if (!readOnly) setRedo('');
        break;
      case 'confirm':
        void confirm();
        break;
      case 'discard':
        void discard();
        break;
      case 'undo':
        void undo();
        break;
    }
  };

  const onPanelKey = (event: KeyboardEvent) => {
    // Keys inside the companion never reach page shortcuts or drawers behind it.
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (target === input.current) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void send();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        if (proposal) gridBox.current?.focus();
        else onClose();
      }
      return;
    }
    if (target.dataset['redo'] !== undefined) {
      if (event.key === 'Enter') {
        event.preventDefault();
        void submitRedo();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setRedo(null);
        gridBox.current?.focus();
      }
      return;
    }
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'u' && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      void undo();
    } else if (event.key === 'i' || event.key === '/') {
      event.preventDefault();
      input.current?.focus();
    }
  };

  const Grid = proposal ? GRID_COMPONENTS[proposal.kind] : null;
  const summary = proposal ? gridSummary(proposal, grid) : null;

  return (
    <aside ref={panel} tabIndex={-1} className={`companion ${proposal ? 'companion-wide' : ''}`} role="dialog" aria-label="Companion" data-companion onKeyDown={onPanelKey}>
      <header className="companion-head">
        <h2>Companion</h2>
        {status?.model ? <span className="muted">{status.provider === 'fake' ? 'fake model (development)' : status.model}</span> : null}
        <span className="companion-head-actions">
          {saved && undoLeft > 0 ? (
            <button type="button" className="btn btn-small" onClick={() => void undo()} title="Undo the last saved change">
              Undo <Kbd>u</Kbd> {Math.floor(undoLeft / 60000)}:{String(Math.floor((undoLeft % 60000) / 1000)).padStart(2, '0')}
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost btn-small" onClick={onClose} aria-label="Close companion">
            Esc
          </button>
        </span>
      </header>

      <div className="companion-body">
        <div className="companion-chat">
          <div className="companion-log" ref={logBox} aria-live="polite">
            {statusError ? <p className="companion-error">{statusError}</p> : null}
            {status && !status.configured ? <p className="companion-error">{status.reason ?? 'The companion is not configured on this environment.'}</p> : null}
            {log.length === 0 && status?.configured ? (
              <div className="companion-hint muted">
                <p>Ask in plain words. Changes come back as a grid you review; nothing is saved until you press <Kbd>⌘/Ctrl Enter</Kbd>.</p>
                <p>
                  e.g. <em>show me all ASOS coupons in UK and update their titles</em>, <em>hide expired Nike codes on Wagsave</em>, <em>expire the ASOS 20% code</em>
                </p>
              </div>
            ) : null}
            {log.map((entry) => (
              <div key={entry.id} className={`companion-msg companion-${entry.kind}${entry.status ? ` companion-tool-${entry.status}` : ''}`}>
                {entry.text}
              </div>
            ))}
            {busy ? <div className="companion-msg companion-tool companion-tool-running">Working…</div> : null}
          </div>
          <label className="companion-input">
            <span className="sr-only">Ask the companion</span>
            <textarea
              ref={input}
              rows={2}
              value={prompt}
              placeholder={status?.configured === false ? 'Not configured' : 'Ask or describe a change…  (Enter to send)'}
              disabled={status?.configured === false}
              onChange={(event) => setPrompt(event.target.value)}
            />
          </label>
        </div>

        {proposal && Grid ? (
          <section className="companion-grid" aria-label={`Proposal ${proposal.ref}`}>
            <div className="companion-grid-head">
              <div>
                <strong>{proposal.summary}</strong>
                <div className="muted">
                  {proposal.reason} · <span className="mono">{proposal.ref}</span> · {proposal.status}
                </div>
                {proposal.error ? <div className="companion-error">{proposal.error}</div> : null}
              </div>
            </div>
            <div className="companion-grid-scroll" data-grid tabIndex={0} ref={gridBox} onKeyDown={onGridKey} aria-label="Proposal grid; j/k move, Enter edit, x include, Cmd+Enter save, Esc discard">
              <Grid proposal={proposal} state={grid} dispatch={dispatch} readOnly={readOnly} />
            </div>
            {redo !== null ? (
              <div className="companion-redo">
                <span>
                  Redo {selectedIds(grid).length} row(s) with your note:
                </span>
                <input data-redo autoFocus value={redo} onChange={(event) => setRedo(event.target.value)} placeholder="e.g. shorter, mention free delivery  (Enter to send, Esc to cancel)" />
              </div>
            ) : null}
            <footer className="companion-grid-foot">
              <span>
                {summary?.included} included · {summary?.changed} changing · {summary?.warnings} with warnings
              </span>
              <span className="companion-keys muted">
                <Kbd>j/k</Kbd> move <Kbd>Enter</Kbd> edit <Kbd>x</Kbd> include <Kbd>a</Kbd> all <Kbd>r</Kbd> redo <Kbd>Esc</Kbd> discard
              </span>
              <button type="button" className="btn btn-ghost" onClick={() => void discard()} disabled={busy}>
                Discard
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void confirm()} disabled={readOnly || !summary?.changed}>
                Save {summary?.changed ?? 0} <Kbd>⌘ Enter</Kbd>
              </button>
            </footer>
          </section>
        ) : null}
      </div>
    </aside>
  );
}
